import { useMutation } from '@tanstack/react-query';
import { isEqual } from 'lodash';

import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import useEditableQuestion from '@/data/hook/question/useEditableQuestion';
import {
  createFillInTheBlankQuestionIntoActivity,
  updateFillInTheBlankQuestionIntoActivity,
} from '@/data/services/question/fillInTheBlankQuestionServices';
import Activity from '@/models/Activity';
import { FillInTheBlankQuestion } from '@/models/Question';
import { getQuestionErrorMessage } from '@/utils/questions/getQuestionErrorMessage';
import { adapterFillInTheBlankOptions } from '@/utils/questions/adapterAnswersDTO';
import {
  adapterFeedbacks,
  sanitizeFeedbacks,
} from '@/utils/questions/adapterFeedbacks';
import { sanitizeFillInTheBlankOptions } from '@/utils/questions/sanitizeAnswers';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import alert from '@/utils/shared/alert';
import FillInTheBlankForm, {
  FillInTheBlankFormFields,
} from './FillInTheBlankForm';

type FillInTheBlankFormServiceProps = {
  activity: Activity;
  question: FillInTheBlankQuestion;
  isActive?: boolean;
};

export default function FillInTheBlankFormService({
  activity,
  question,
  isActive,
}: FillInTheBlankFormServiceProps) {
  const gradeCtx = useQuestionExamGradeCtx();
  const { ensureEditable, resolveEditable } = useEditableQuestion(activity);

  const handleCreate = async (
    data: FillInTheBlankFormFields,
  ): Promise<FillInTheBlankQuestion> => {
    const { maxGrade, ...rest } = data;
    const editableActivity = await ensureEditable();
    const saved = await createFillInTheBlankQuestionIntoActivity(
      editableActivity.id,
      {
        type: question.type,
        identifier: question.identifier,
        order: question.order,
        ...rest,
        options: sanitizeFillInTheBlankOptions(rest.options),
        feedbacks: sanitizeFeedbacks(rest.feedbacks),
      },
    );
    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const handleUpdate = async (
    data: FillInTheBlankFormFields,
  ): Promise<FillInTheBlankQuestion> => {
    const { maxGrade, ...rest } = data;
    const oldValues: Omit<FillInTheBlankFormFields, 'maxGrade'> = {
      title: normalizeTranslatableField(question.title),
      context: normalizeTranslatableField(question.context),
      question: question.question ?? '',
      options: adapterFillInTheBlankOptions(question.options ?? []),
      feedbacks: adapterFeedbacks(question.feedbacks ?? []),
    };

    const changedFields: Partial<Omit<FillInTheBlankFormFields, 'maxGrade'>> =
      {};
    if (!isEqual(oldValues.title, rest.title)) {
      changedFields.title = rest.title;
    }
    if (!isEqual(oldValues.context, rest.context)) {
      changedFields.context = rest.context;
    }
    if (!isEqual(oldValues.question, rest.question)) {
      changedFields.question = rest.question;
    }

    const newSanitizedOptions = sanitizeFillInTheBlankOptions(rest.options);
    const oldSanitizedOptions = sanitizeFillInTheBlankOptions(
      oldValues.options,
    );
    if (!isEqual(oldSanitizedOptions, newSanitizedOptions)) {
      changedFields.options = newSanitizedOptions;
    }

    const newSanitizedFeedbacks = sanitizeFeedbacks(rest.feedbacks);
    const oldSanitizedFeedbacks = sanitizeFeedbacks(oldValues.feedbacks);
    if (!isEqual(oldSanitizedFeedbacks, newSanitizedFeedbacks)) {
      changedFields.feedbacks = newSanitizedFeedbacks;
    }

    const { toClone, updateTarget } = await resolveEditable(question);
    if (changedFields.options)
      changedFields.options = toClone(changedFields.options, q => q.options);
    const saved = await updateTarget(
      changedFields,
      updateFillInTheBlankQuestionIntoActivity,
    );

    if (maxGrade !== undefined) {
      await gradeCtx?.saveGrade({ question: saved, maxGrade });
    }
    return saved;
  };

  const { mutateAsync: submitQuestion, isPending } = useMutation({
    mutationFn: (data: FillInTheBlankFormFields) =>
      !question.id ? handleCreate(data) : handleUpdate(data),
    onError: err => alert.error(getQuestionErrorMessage(err)),
  });

  return (
    <FillInTheBlankForm
      key={question.identifier}
      isActive={isActive}
      question={question}
      onSubmit={submitQuestion}
      isLoading={isPending}
    />
  );
}
